import { useRef } from 'react';
import { useCountUp } from '../hooks/useCountUp';
import { formatPoints } from '../utils/redeemUtils';

type RedeemStatsSectionProps = {
  points: number;
  tickets: number;
  streak: number;
  onScrollToRent: () => void;
  onScrollToGiftCards: () => void;
  onGoEarn: () => void;
};

const StreakIcon = () => (
  <svg
    className='rd-stat-icon'
    viewBox='0 0 24 24'
    fill='none'
    stroke='var(--color-green)'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2' />
  </svg>
);

export const RedeemStatsSection = ({
  points,
  tickets,
  streak,
  onScrollToRent,
  onScrollToGiftCards,
  onGoEarn,
}: RedeemStatsSectionProps) => {
  const animatedPoints = useCountUp(points, 1200);
  const animatedTickets = useCountUp(tickets, 1000);
  const activeDots = Math.min(6, streak);
  const streakCardRef = useRef<HTMLDivElement>(null);

  const handleStreakMouseEnter = () => {
    const dots = streakCardRef.current?.querySelectorAll<HTMLElement>('.rd-streak-dots span');
    dots?.forEach((dot, index) => {
      dot.style.animation = 'none';
      void dot.offsetHeight;
      dot.style.animation = `rdDotPulse 0.55s ease-out ${index * 0.08}s both`;
    });
  };

  return (
    <div className='rd-stats'>
      <div className='rd-stat rd-stat-featured'>
        <div className='rd-stat-particle-layer' aria-hidden='true'>
          <span className='rd-stat-particle p1' />
          <span className='rd-stat-particle p2' />
          <span className='rd-stat-particle p3' />
        </div>
        <div className='rd-stat-lbl'>Points balance</div>
        <div className='rd-stat-val-row'>
          <div className='rd-stat-val'>{formatPoints(animatedPoints)}</div>
        </div>
        <div className='rd-stat-sub'>Points</div>
        <div className='rd-stat-foot'>
          <button type='button' className='rd-stat-cta' onClick={onScrollToRent}>
            Redeem
          </button>
          <button type='button' className='rd-stat-hint rd-stat-hint-link' onClick={onScrollToGiftCards}>
            115+ brands
          </button>
        </div>
      </div>

      <div className='rd-stat rd-stat-gold'>
        <div className='rd-stat-spark-layer' aria-hidden='true'>
          <span className='rd-stat-spark s1' />
          <span className='rd-stat-spark s2' />
          <span className='rd-stat-spark s3' />
          <span className='rd-stat-spark s4' />
        </div>
        <div className='rd-stat-lbl'>Drop tickets</div>
        <div className='rd-stat-val rd-stat-val-shimmer'>{formatPoints(animatedTickets)}</div>
        <div className='rd-stat-sub'>Tickets this month</div>
        <div className='rd-stat-foot'>
          <button
            type='button'
            className='rd-stat-cta'
            onClick={onGoEarn}
          >
            + Earn more tickets
          </button>
        </div>
      </div>

      <div className='rd-stat rd-stat-green' ref={streakCardRef} onMouseEnter={handleStreakMouseEnter}>
        <div className='rd-stat-lbl'>Payment streak</div>
        <div className='rd-stat-val-row'>
          <StreakIcon />
          <div className='rd-stat-val'>{formatPoints(streak)}</div>
        </div>
        <div className='rd-stat-sub'>Months on time</div>
        <div className='rd-stat-foot'>
          <div className='rd-streak-dots' aria-hidden='true'>
            {Array.from({ length: 6 }).map((_, index) => (
              <span key={index} className={index < activeDots ? 'active' : undefined} />
            ))}
          </div>
          <span className='rd-stat-hint'>Pay to build streak</span>
        </div>
      </div>
    </div>
  );
};
